import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form, Modal, Select, Space } from 'antd';
import { debounce } from 'lodash';
import { observer } from 'mobx-react-lite';
import { ReactComponent as MoveOutIcon } from 'assets/icons/move-white.svg';
import { FormWrapper } from 'common/components/form-wrapper/form-wrapper';
import { IModal } from 'common/models/model.interface';
import citationService from 'common/services/citation-service/citation.service';
import residentsService from 'common/services/residents-service/residents.service';
import { IResidentRead } from 'common/services/residents-service/residents.service.types';
import { notification } from 'common/utils/notification';

interface MoveCitationModalProps {
  citationId: number;
}

interface MoveCitationFormValues {
  residentId: number;
}

const MoveCitationModal: FC<IModal<MoveCitationModalProps, boolean>> = observer(({ isOpen, onClose, title, initData }) => {
  const [form] = Form.useForm<MoveCitationFormValues>();
  const [residents, setResidents] = useState<IResidentRead[]>([]);
  const [isLoadingResidents, setIsLoadingResidents] = useState(false);
  const requestIdRef = useRef(0);

  const searchResidents = useCallback(
    debounce(async (value = '') => {
      const requestId = ++requestIdRef.current;
      const filter = value ? `firstName:*${value} OR lastName:*${value}` : null;

      setIsLoadingResidents(true);
      try {
        const result = await residentsService.getResidents(null, filter, '+lastName');
        if (requestId === requestIdRef.current) {
          setResidents(result.items);
        }
      } catch {
        if (requestId === requestIdRef.current) {
          setResidents([]);
          notification.error({ message: 'Resident search failed.' });
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoadingResidents(false);
        }
      }
    }, 350),
    [],
  );

  useEffect(() => {
    return () => searchResidents.cancel();
  }, [searchResidents]);

  const hideModal = (isSuccess: boolean) => {
    requestIdRef.current += 1;
    searchResidents.cancel();
    form.resetFields();
    setResidents([]);
    setIsLoadingResidents(false);
    onClose?.(isSuccess);
  };

  const onFinish = async ({ residentId }: MoveCitationFormValues) => {
    if (!initData?.citationId) {
      notification.error({ message: 'Citation id is missing.' });
      return;
    }

    await citationService.move(initData.citationId, { residentId });
    hideModal(true);
  };

  return (
    <Modal centered title={title} width={450} open={isOpen} wrapClassName="custom-modal" closable={false} footer={null}>
      <FormWrapper form={form} onFinish={onFinish} layout="vertical">
        <Space className="full-width">
          <Form.Item name="residentId" label="Resident" rules={[{ required: true, message: 'Please select a resident.' }]}>
            <Select
              placeholder="Select resident"
              defaultActiveFirstOption={false}
              showSearch
              allowClear
              loading={isLoadingResidents}
              onSearch={searchResidents}
              filterOption={false}
              onDropdownVisibleChange={(dropdownOpen) => dropdownOpen && residents.length === 0 && searchResidents()}
              options={residents.map((resident) => ({
                label: `${resident.firstName} ${resident.lastName}`,
                value: resident.id,
              }))}
            />
          </Form.Item>
        </Space>
        <Space className="footer">
          <Button type="link" onClick={() => hideModal(false)}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" icon={<MoveOutIcon />}>
            Move
          </Button>
        </Space>
      </FormWrapper>
    </Modal>
  );
});

export default MoveCitationModal;
